import { Directive, ElementRef, HostListener, inject } from '@angular/core';

/**
 * Restricts input values to non-negative decimal numbers (digits and a single dot).
 */
@Directive({
  selector: '[lDecimalInput]',
  standalone: true,
})
export class DecimalInputDirective {
  private readonly elementRef = inject(ElementRef<HTMLInputElement>);

  @HostListener('input')
  public onInput(): void {
    const input = this.elementRef.nativeElement;
    const sanitized = this.sanitize(input.value);

    if (input.value === sanitized) {
      return;
    }

    input.value = sanitized;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End',
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (/^[0-9.]$/.test(event.key)) {
      if (event.key === '.' && inputContainsDot(this.elementRef.nativeElement.value)) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
  }

  private sanitize(value: string): string {
    const digitsAndDot = value.replaceAll(/[^0-9.]/g, '');
    const [integerPart = '', ...decimalParts] = digitsAndDot.split('.');

    if (!decimalParts.length) {
      return integerPart;
    }

    return `${integerPart}.${decimalParts.join('')}`;
  }
}

function inputContainsDot(value: string): boolean {
  return value.includes('.');
}
